import { ApiError, isStepUpRequired } from "./step-up.js";

// View derivations for the platform-admin "Signer & keys" page. Response types mirror
// apps/edge/src/platform-admin.ts (GET /api/v1/platform/signer and /api/v1/platform/signing-keys).

export type Tone = "ok" | "warning" | "critical" | "muted";
export interface Pill { tone: Tone; label: string }

export interface SignerReport {
  configured: boolean;
  configuration: { signer_binding: boolean; service_token: boolean; signer_url: boolean; receipt_key_id: boolean };
  reachable: boolean;
  latency_ms: number | null;
  health: { status: string | null; service: string | null; receipt_key_id: string | null; tpm_attestation_verifier: boolean | null } | null;
  status: {
    receipt_key_id: string | null;
    transport: string | null;
    idempotency: string | null;
    registry: { loaded: boolean; payload_sha256: string | null; issued_at: string | null; expires_at: string | null; tenants: number | null };
    providers: string[];
  } | null;
  status_error: string | null;
  receipt_key_id_matches: boolean | null;
  registry_expires_in_seconds: number | null;
  warnings: string[];
  checked_at: string;
}

export type KeyStatus = "valid" | "not_yet_valid" | "expired" | "unconfigured" | "incomplete";
export interface SigningKey {
  purpose: "bootstrap" | "policy" | "registration" | "audit" | "signer_receipt" | "registry";
  label: string;
  key_id: string | null;
  algorithm: "Ed25519";
  public_key: string | null;
  public_key_encoding: "spki_base64" | "raw_base64" | "raw_base64url";
  public_key_source: "configured" | "derived_from_private" | null;
  fingerprint_sha256: string | null;
  not_before: string | null;
  expires_at: string | null;
  days_remaining: number | null;
  status: KeyStatus;
  private_key_configured: boolean | "n/a";
  pair_valid: boolean | null;
  problems: string[];
  published_in_release?: boolean | null;
}
export interface SigningKeysReport {
  release_version: string;
  release_trust_url: string | null;
  keys: SigningKey[];
  private_keys_returned: false;
  checked_at: string;
}

export const KEY_EXPIRY_WARNING_DAYS = 30;

export function durationLabel(seconds: number): string {
  const abs = Math.abs(seconds);
  if (abs >= 86_400) return `${Math.floor(abs / 86_400)} d`;
  if (abs >= 3_600) return `${Math.floor(abs / 3_600)} h`;
  if (abs >= 60) return `${Math.floor(abs / 60)} min`;
  return `${Math.floor(abs)} s`;
}

export function relativeTime(instant: string | null | undefined, now: Date = new Date()): string {
  if (!instant) return "—";
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return "—";
  const seconds = Math.round((parsed - now.getTime()) / 1000);
  if (Math.abs(seconds) < 60) return "just now";
  return seconds > 0 ? `in ${durationLabel(seconds)}` : `${durationLabel(seconds)} ago`;
}

export interface WarningView { code: string; severity: "critical" | "warning"; title: string; detail: string }

const warningCatalog: Record<string, Omit<WarningView, "code">> = {
  signer_unreachable: { severity: "critical", title: "Signer is unreachable", detail: "The edge could not reach the protected signer health endpoint over its service binding. Issuance that needs the signer will fail." },
  registry_missing: { severity: "critical", title: "Tenant registry is not loaded", detail: "The signer has no verified tenant registry, so it will refuse tenant-bound signing requests." },
  registry_expired: { severity: "critical", title: "Tenant registry has expired", detail: "Publish a freshly signed registry to the signer; it rejects requests against an expired registry." },
  receipt_key_mismatch: { severity: "critical", title: "Signer receipt key does not match", detail: "The signer reports a receipt key ID different from REMOTE_SIGNER_RECEIPT_KEY_ID. The edge will reject its receipts." },
  signer_status_unavailable: { severity: "warning", title: "Signer status is unavailable", detail: "Health responded but the authenticated status call failed. Check the service token and the signer version." },
  registry_expiring_soon: { severity: "warning", title: "Tenant registry expires soon", detail: "The registry expires within three hours. Confirm the registry refresh job is running." },
  signer_not_configured: { severity: "warning", title: "Signer is not fully configured", detail: "The SIGNER binding, REMOTE_SIGNER_URL, or REMOTE_SIGNER_TOKEN is missing on this edge." },
};

export function signerWarnings(codes: string[]): WarningView[] {
  const views = codes.map((code) => ({ code, ...(warningCatalog[code] ?? { severity: "warning" as const, title: code.replaceAll("_", " "), detail: "Reported by the edge." }) }));
  return views.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1));
}

export function signerSummary(report: SignerReport): Pill {
  if (!report.configured) return { tone: "critical", label: "Signer not configured" };
  if (!report.reachable) return { tone: "critical", label: "Signer unreachable" };
  const views = signerWarnings(report.warnings);
  if (views.some((view) => view.severity === "critical")) return { tone: "critical", label: "Signer degraded" };
  if (views.length) return { tone: "warning", label: "Signer needs attention" };
  return { tone: "ok", label: "Signer healthy" };
}

export function keyStatusPill(key: SigningKey): Pill {
  switch (key.status) {
    case "expired": return { tone: "critical", label: "Expired" };
    case "incomplete": return { tone: "critical", label: "Incomplete" };
    case "not_yet_valid": return { tone: "warning", label: "Not yet valid" };
    case "unconfigured": return { tone: "muted", label: "Not configured" };
    case "valid":
      if (key.days_remaining !== null && key.days_remaining <= KEY_EXPIRY_WARNING_DAYS) {
        return { tone: "warning", label: key.days_remaining <= 0 ? "Expires today" : `Expires in ${key.days_remaining} d` };
      }
      return { tone: "ok", label: "Valid" };
  }
}

export function pairCheckView(key: SigningKey): Pill {
  if (key.pair_valid === true) return { tone: "ok", label: "Pair verified" };
  if (key.pair_valid === false) return { tone: "critical", label: "Pair mismatch" };
  if (key.private_key_configured === "n/a") return { tone: "muted", label: "Held by signer" };
  if (key.public_key_source === "derived_from_private") return { tone: "muted", label: "Derived from private key" };
  return { tone: "muted", label: "Not checked" };
}

export function releaseView(published: boolean | null | undefined, version: string): Pill | undefined {
  if (published === undefined) return undefined;
  if (published === true) return { tone: "ok", label: `Published in ${version}` };
  if (published === false) return { tone: "critical", label: `Missing from ${version}` };
  return { tone: "muted", label: "Release check unavailable" };
}

export function shortFingerprint(hex: string | null): string {
  if (!hex) return "—";
  return hex.length > 16 ? `${hex.slice(0, 8)}…${hex.slice(-8)}` : hex;
}

export function validityLabel(key: SigningKey): string {
  if (!key.not_before && !key.expires_at) return "No expiry configured";
  const day = (value: string | null) => (value ? value.slice(0, 10) : "—");
  const window = `${day(key.not_before)} → ${day(key.expires_at)}`;
  if (key.days_remaining === null) return window;
  return key.days_remaining < 0 ? `${window} · expired ${-key.days_remaining} d ago` : `${window} · ${key.days_remaining} d left`;
}

/**
 * Interprets a platform API response. Cloudflare Access answers an expired session with a login page
 * (not JSON) or a 401, which needs a fresh Access sign-in rather than a retry.
 */
export function platformResponse<T>(status: number, body: unknown): T {
  if (body === undefined || body === null || typeof body !== "object" || status === 401) {
    throw new ApiError("Your platform admin session has expired. Sign in again to continue.", "access_required");
  }
  const error = (body as { error?: { message?: string; code?: string } }).error;
  if (status < 200 || status >= 300) throw new ApiError(error?.message ?? "Platform status could not be loaded", error?.code);
  return body as T;
}

export function needsReauthentication(error: unknown): boolean {
  return isStepUpRequired(error) || (error instanceof ApiError && error.code === "access_required");
}

// POST /api/v1/platform/signer/registry/publish: the publication result plus the diagnostic events it logged.
export interface PublishResponse {
  result:
    | { status: "published"; tenants: number; failed_tenants: string[]; issued_at: string; expires_at: string; payload_sha256: string }
    | { status: "skipped"; reason: string; failed_tenants?: string[] }
    | { status: "rejected"; http_status: number; failed_tenants: string[] };
  events: Array<Record<string, unknown>>;
}

export function publishOutcome(response: PublishResponse): { tone: Tone; title: string; details: string[] } {
  const details: string[] = [];
  for (const event of response.events) {
    const tenant = typeof event.tenant_id === "string" ? event.tenant_id : "";
    if (event.event === "signer_registry.tenant_warnings" && Array.isArray(event.warnings))
      for (const warning of event.warnings) details.push(`${tenant}: ${String(warning)}`);
    if (event.event === "signer_registry.tenant_failed") details.push(`${tenant}: ${String(event.error)}`);
    if (event.event === "signer_registry.rejected") details.push(`signer: ${String(event.response ?? "")}`);
  }
  const { result } = response;
  if (result.status === "published")
    return { tone: result.failed_tenants.length ? "warning" : "ok", title: `Registry published for ${result.tenants} tenant${result.tenants === 1 ? "" : "s"}`, details };
  if (result.status === "skipped") return { tone: "critical", title: `Registry not published: ${result.reason}`, details };
  return { tone: "critical", title: `Signer rejected the registry (HTTP ${result.http_status})`, details };
}
